<template>
	<div class="address-form">

		<VSTextField
			id="first_name"
			class="address-form__item --half-desktop"
			label="First name"
			type="text"
			:disabled="submittingOrder"
			:value="address.first_name"
			:error-message="
				fieldHasError('first_name') && 'First name is required'
			"
			@input="(value) => onChange('first_name', value)"
		/>

		<VSTextField
			id="last_name"
			class="address-form__item --half-desktop"
			label="Last name"
			type="text"
			:disabled="submittingOrder"
			:value="address.last_name"
			:error-message="
				fieldHasError('last_name') && 'Last name is required'
			"
			@input="(value) => onChange('last_name', value)"
		/>

		<slot name="email-field"></slot>

		<VSTextField
			v-if="false"
			id="company_name"
			class="address-form__item"
			label="Company name (optional)"
			type="text"
			:disabled="submittingOrder"
			:value="address.company_name"
			@input="(value) => onChange('company_name', value)"
		/>

		<VSTextField
			id="address"
			class="address-form__item"
			label="Address line 1"
			type="text"
			:disabled="submittingOrder"
			:value="address.address"
			:error-message="
				fieldHasError('address') && 'Address line 1 is required'
			"
			@input="(value) => onChange('address', value)"
		/>

		<div v-if="!renderLineTwo" class="address-form__item">
			<button class="address-form__action" @click="renderLineTwo = true">
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
					<path
						d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"
					/>
				</svg>
				Add apartment, suite, etc.
			</button>
		</div>

		<VSTextField
			v-if="renderLineTwo"
			id="address2"
			class="address-form__item"
			label="Address line 2"
			type="text"
			:disabled="submittingOrder"
			:value="address.address2"
			@input="(value) => onChange('address2', value)"
		/>

		<VSTextField
			id="city"
			class="address-form__item --third-desktop"
			label="City"
			type="text"
			:disabled="submittingOrder"
			:error-message="fieldHasError('city') && 'City is required'"
			:value="address.city"
			@input="(value) => onChange('city', value)"
		/>

		<VSSelect
			v-if="isUSAOrCanada"
			id="state"
			class="address-form__item --third-desktop"
			:label="stateOrProvinceLabel"
			:value="address.state"
			:options="stateOrProvinceOptions"
			text-field="name"
			value-field="abbreviation"
			:disabled="disableCountryAndState || submittingOrder"
			:error-message="
				fieldHasError('state') && `${stateOrProvinceLabel} is required`
			"
			@input="(value) => onChange('state', value)"
		>
		</VSSelect>

		<VSTextField
			v-else
			id="state"
			class="address-form__item --third-desktop"
			:label="stateOrProvinceLabel"
			type="text"
			:disabled="disableCountryAndState || submittingOrder"
			:value="address.state"
			:error-message="
				fieldHasError('state') && `${stateOrProvinceLabel} is required`
			"
			@input="(value) => onChange('state', value)"
		/>

		<VSTextField
			id="postal_code"
			class="address-form__item --third-desktop"
			:label="zipOrPostalCodeLabel"
			type="text"
			:disabled="submittingOrder"
			:value="address.postal_code"
			:error-message="
				postalCodeState && `${zipOrPostalCodeLabel} is required`
			"
			@input="setPostalCode"
			@blur="prefillCountryAndState"
		/>

		<VSSelect
			id="country"
			class="address-form__item"
			label="Country"
			:value="address.country"
			:options="filteredCountries"
			:disabled="disableCountryAndState || submittingOrder"
			:error-message="fieldHasError('country') && 'Country is required'"
			@input="setCountry"
		>
		</VSSelect>

		<VSPhoneField
			id="phone_number"
			class="address-form__item"
			label="Phone"
			:disabled="submittingOrder"
			:value="address.phone_number"
			context="In case we need to contact you about your order"
			:error-message="
				fieldHasError('phone_number') && 'Phone is required'
			"
			@input="(value) => onChange('phone_number', value)"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@js/api';
import { VSTextField, VSSelect, VSPhoneField } from '@component/UI/controls';
import {
	checkPostalCodeRegex,
	USA_CANADA_COUNTRY_CODES,
	USA_COUNTRY_CODE,
	CAN_COUNTRY_CODE,
} from '@js/service/checkoutService';
import countries from '../../../../../data/iso3166-countries-3.json';
import states from '../../../../../data/us-states.json';
import provinces from '../../../../../data/can-provinces.json';

export default {
	components: {
		VSTextField,
		VSSelect,
		VSPhoneField,
	},
	props: {
		address: {
			type: Object,
			required: true,
		},
		allowInternational: {
			type: Boolean,
			default: false,
		},
		errors: {
			type: Array,
			required: true,
		},
		postalCodeMismatchError: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			disableCountryAndState: false,
			renderLineTwo: false,
		};
	},

	computed: {
		...mapState('checkout', ['submittingOrder']),

		filteredCountries() {
			const availableCountries = this.allowInternational
				? countries
				: countries.filter((country) =>
						USA_CANADA_COUNTRY_CODES.includes(country['alpha-3'])
				  );

			const topCountries = this.allowInternational
				? USA_CANADA_COUNTRY_CODES.map((c) =>
						availableCountries.find(
							(country) => country['alpha-3'] === c
						)
				  ).concat({ name: '---', 'alpha-3': '' })
				: [];

			return [...topCountries, ...availableCountries].map((c) => ({
				text: c.name,
				value: c['alpha-3'],
			}));
		},
		isUSA() {
			return this.address.country === USA_COUNTRY_CODE;
		},
		isCanada() {
			return this.address.country === CAN_COUNTRY_CODE;
		},
		isUSAOrCanada() {
			return this.isUSA || this.isCanada;
		},
		stateOrProvinceOptions() {
			if (this.isUSA) return states;
			if (this.isCanada) return provinces;
			return [];
		},
		stateOrProvinceLabel() {
			if (this.isUSA) return 'State';
			if (this.isCanada) return 'Province';
			return 'Region';
		},
		zipOrPostalCodeLabel() {
			if (this.isUSA) return 'ZIP code';
			return 'Postal code';
		},
		postalCodeState() {
			return this.errors.includes('postal_code') ||
				this.postalCodeMismatchError
				? true
				: null;
		},
	},

	methods: {
		onChange(key, value) {
			this.$emit('change', {
				...this.address,
				[key]: value,
			});
		},
		setCountry(value) {
			const newAddress = {
				...this.address,
				country: value,
				state: undefined,
			};

			const valid = checkPostalCodeRegex(newAddress);

			this.$emit('change', {
				...newAddress,
				postal_code: valid ? newAddress.postal_code : undefined,
			});
		},
		async prefillCountryAndState() {
			if (this.address.postal_code.length !== 5) return;

			this.disableCountryAndState = true;

			const data = await api.lookupAddressFromZipCode(
				this.address.postal_code
			);

			if (!data) {
				this.disableCountryAndState = false;
				return;
			}

			// We can only accept 3 letter country codes
			if(data.data.country.length > 0 && data.data.country.length !== 3) {
				this.disableCountryAndState = false;
				return;
			}

			this.$emit('change', {
				...this.address,
				country: data.data.country,
				state: data.data.state,
			});

			this.disableCountryAndState = false;
		},
		fieldHasError(key) {
			return this.errors.includes(key);
		},
		setPostalCode(value) {
			this.onChange('postal_code', value);
			this.$emit('postalCodeUpdated');
		},
	},
};
</script>

<style lang="scss">
.address-form {
	display: grid;
	grid-template-columns: repeat(12, 1fr);
	gap: var(--size-base);

	&__item {
		grid-column: span 12;

		&.--half-desktop {
			@media screen and (min-width: 992px) {
				grid-column: span 6;
			}
		}

		&.--third-desktop {
			@media screen and (min-width: 992px) {
				grid-column: span 4;
			}
		}
	}

	&__action {
		background: transparent;
		border: 0;
		color: var(--blue-600);
		display: inline-flex;
		font-family: inherit;
		font-size: var(--size-base);
		gap: var(--size-sm);
		text-align: left;
		align-items: center;
		transition: color 150ms ease;
		align-self: start;

		&:focus {
			box-shadow: 0 0 0 1px var(--blue-600);
		}

		&:hover {
			cursor: pointer;
			color: var(--blue-700);
		}

		> svg {
			fill: currentColor;
			height: var(--size-base);
		}
	}
}
</style>
